import Pane from 'components/Common/Pane'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import React, { useEffect } from 'react'
import LuxuryEscapesSmallLogo from '../Header/Logo/logos/LuxuryEscapesSmallLogo'
import { DevPanelData } from './types'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import useStateWithSetters from 'hooks/useStateWithSetters'
import ContextsTab from './tabs/ContextsTab'
import ReduxStateTab from './tabs/ReduxStateTab'

const RootLayout = styled(Group)`
  min-height: 100vh;

  & > * + * {
    border-top: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  }
`

const HorizontalLayout = styled(Group)`
  flex: 1;

  & > * + * {
    border-left: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  }
`

const Logo = styled(LuxuryEscapesSmallLogo)`
  height: ${rem(28)};
  width: ${rem(28)};
`

const Sidebar = styled.div`
  flex: 0 0 auto;
`

const Tabs = styled.div`
  position: sticky;
  top: 0;
`

const Main = styled.div`
  flex: 1;
`

const tabs = [
  { id: 'contexts', label: 'Contexts', component: ContextsTab },
  { id: 'redux', label: 'Redux state', component: ReduxStateTab },
] as const

const tabIds = tabs.map(tab => tab.id)

const TAB_STORAGE_KEY = 'DevPanel:selectedTab'

interface Props {
  state: DevPanelData
}

function DevPanelRoot({ state }: Props) {
  const [currentTabId, setTab] = useStateWithSetters(tabIds, () => {
    try {
      const saved = sessionStorage.getItem(TAB_STORAGE_KEY) as typeof tabIds[number] | null
      return saved ?? 'contexts'
    } catch {
      return 'contexts'
    }
  })

  useEffect(() => {
    try {
      sessionStorage.setItem(TAB_STORAGE_KEY, currentTabId)
    } catch {}
  }, [currentTabId])

  const currentTab = tabs.find(tab => tab.id === currentTabId)!
  const TabContent = currentTab.component

  return <RootLayout direction="vertical">
    <Pane padding={20}>
      <HeadingTextBlock variant="heading3" startIcon={<Logo />}>Customer portal dev panel</HeadingTextBlock>
    </Pane>

    <HorizontalLayout direction="horizontal">
      <Sidebar>
        <Tabs>
          {tabs.map(tab => (
            <ClickableListItem key={tab.id} onClick={setTab[tab.id]} title={tab.label} selected={tab.id === currentTabId} />
          ))}
        </Tabs>
      </Sidebar>

      <Main>
        <TabContent state={state} />
      </Main>
    </HorizontalLayout>
  </RootLayout>
}

export default DevPanelRoot
